/// <reference types="vite-plugin-svgr/client" />
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Flex } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { useIsMutating } from '@tanstack/react-query'

import { AdvisorsListRoutePathsEnum } from '@/modules/advisorsPage/routes/advisorsListRouteConfig.types'

import { budgetFormSchema } from '@/components/BudgetForm/BudgetForm.schema'
import type { BudgetFormProps } from '@/components/BudgetForm/BudgetForm.types'
import { Button } from '@/components/Button/Button'
import { Disclaimer } from '@/components/Disclaimer/Disclaimer'
import { NumberInput } from '@/components/NumberInput/NumberInput'
import { SelectInput } from '@/components/SelectInput/SelectInput'
import { Text } from '@/components/Text/Text'
import { Title } from '@/components/Title/Title'

import { BUDGET_CATEGORY_OPTIONS } from '@/constants/budgetCategories.constants'

import { formatMoney } from '@/helpers/formatMoney.helper'

import WhistleIcon from '@/assets/icons/whistle.svg?react'

import classes from './BudgetForm.module.css'

export function BudgetForm({
  currentBudget,
  data,
  edit,
  onClose,
  onSubmit,
}: BudgetFormProps) {
  const navigate = useNavigate()

  const form = useForm({
    initialValues: edit ? data : undefined,
    validate: zodResolver(budgetFormSchema),
  })

  const isMutating = useIsMutating({
    exact: true,
  })

  const handleSubmit = form.onSubmit(values => onSubmit(values))

  const handleNavigateAdvisorsList = () => {
    navigate(AdvisorsListRoutePathsEnum.ADVISORS_LIST)

    onClose()
  }

  useEffect(() => {
    if (data && edit) {
      form.setValues(data)
      form.resetDirty(data)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  return (
    <Box>
      <Flex align="center" direction="column">
        <Title mb={8} variant="h1">
          {edit ? 'Edit' : 'New'} Budget
        </Title>

        <Text align="center">
          Keep your spending in check to achieve your financial and personal
          goals!
        </Text>
      </Flex>

      <Box className={classes.currentBudget}>
        <Text mb={8} variant="body_4_semibold">
          Current budget
        </Text>

        <Text variant="body_1_semibold">
          {formatMoney(currentBudget, 'USD')}
        </Text>
      </Box>

      <Box component="form" mt={16} onSubmit={handleSubmit}>
        <NumberInput
          hideControls
          label="Monthly amount"
          {...form.getInputProps('monthlyAmount')}
        />

        <SelectInput
          {...form.getInputProps('category')}
          data={BUDGET_CATEGORY_OPTIONS}
          label="Category"
          placeholder="Select category"
        />

        <Box mt={32}>
          <Disclaimer
            description={
              <>
                <Text>Need help setting your budget?</Text>

                <Text variant="body_2_black">
                  Schedule a call with your planner!
                </Text>

                <Button
                  h={34}
                  miw="auto"
                  mt={16}
                  onClick={handleNavigateAdvisorsList}
                  variant="outline"
                >
                  Schedule
                </Button>
              </>
            }
            icon={<WhistleIcon />}
          />
        </Box>

        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={16}
          justify={{ base: 'center', md: 'flex-start' }}
          mt={32}
        >
          <Button miw="48%" onClick={onClose} variant="outline">
            Cancel
          </Button>

          <Button loading={!!isMutating} miw="48%" type="submit">
            {!edit ? 'Create' : 'Save'}
          </Button>
        </Flex>
      </Box>
    </Box>
  )
}
